import i18n from "i18next";

/**
 * API sometimes returns a keyed record, e.g. `{ "0": "year", "1": "month" }`, instead of an array.
 */
function coerceKeyedStringRecordToArray(value) {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }
  const vals = Object.values(value);
  if (vals.length === 0) return [];
  if (vals.every((v) => typeof v === "string")) {
    return vals;
  }
  return [];
}

/**
 * Resolves payload to an array of periodicity entries (strings or rich objects).
 */
function resolvePeriodicityListFromPayload(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;

  if (typeof data === "object") {
    const nested =
      data.items ??
      data.results ??
      data.data ??
      data.periodicities ??
      data.periodicity;

    if (Array.isArray(nested)) return nested;
    if (typeof nested === "string" && nested.trim() !== "") {
      return [nested];
    }
    if (nested != null && typeof nested === "object") {
      const fromNested = coerceKeyedStringRecordToArray(nested);
      if (fromNested.length) return fromNested;
    }
    const fromRoot = coerceKeyedStringRecordToArray(data);
    if (fromRoot.length) return fromRoot;
  }

  return [];
}

/**
 * Human-readable label for periodicity values in selects.
 * Keeps value untouched, only prettifies visible text.
 */
export function prettifyTaxSettingPeriodicityLabel(label) {
  if (label == null) return "";
  const raw = String(label).trim();
  if (!raw) return "";
  const key = raw.toLowerCase();
  const known = {
    year: ["year", "Year"],
    quarter: ["quarter", "Quarter"],
    month: ["month", "Month"],
    "bi-annual": ["biAnnual", "Bi-Annual"],
    "biannual": ["biAnnual", "Bi-Annual"],
  };
  if (known[key]) {
    const [i18nKey, fallback] = known[key];
    return i18n.t(`sharedUi.periodicity.${i18nKey}`, { defaultValue: fallback }) ?? fallback;
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * Normalizes GET /countries/tax_setting_periodicity/ response into Polaris Select options.
 */
export function normalizeTaxSettingPeriodicityResponse(data) {
  const list = resolvePeriodicityListFromPayload(data);
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item, i) => {
    if (typeof item === "string") {
      return { value: item, label: prettifyTaxSettingPeriodicityLabel(item) };
    }
    if (item && typeof item === "object") {
      const value = String(
        item.id ?? item.value ?? item.code ?? item.periodicity ?? i
      );
      const rawLabel = String(
        item.name ??
          item.label ??
          item.title ??
          item.description ??
          value
      );
      return { value, label: prettifyTaxSettingPeriodicityLabel(rawLabel) };
    }
    return {
      value: String(item),
      label: prettifyTaxSettingPeriodicityLabel(String(item)),
    };
  });
}

/**
 * If API returns [], show a single "-" placeholder option (value and label).
 * On save, map that to `null` via {@link periodicityToApiValue}.
 */
const EMPTY_PERIODICITY_FALLBACK = { value: "-", label: "-" };

/** Placeholder and empty string are not sent to the API. */
export function periodicityToApiValue(value) {
  if (value == null || value === "" || value === "-") {
    return null;
  }
  return String(value);
}

export function applyEmptyPeriodicityFallback(options) {
  if (!Array.isArray(options) || options.length > 0) {
    return options || [];
  }
  return [EMPTY_PERIODICITY_FALLBACK];
}

/**
 * Value for Assigned Reporting Schedule Select: only what is already stored.
 * On open, empty until the user picks an option (no auto-fill).
 */
export function getEffectivePeriodicitySelectValue(stored) {
  if (stored != null && String(stored).trim() !== "") {
    return String(stored);
  }
  return "";
}

export function buildTaxSettingPeriodicityQuery(overrides = {}) {
  const out = {};
  if (overrides.country_id != null && overrides.country_id !== "") {
    out.country_id = String(overrides.country_id);
  }
  if (overrides.state_id != null && overrides.state_id !== "") {
    out.state_id = String(overrides.state_id);
  }
  if (
    overrides.reason_for_registration_id != null &&
    overrides.reason_for_registration_id !== ""
  ) {
    const n = Number(overrides.reason_for_registration_id);
    if (!Number.isNaN(n)) out.reason_for_registration_id = n;
  }
  if (overrides.epr_category_id != null && overrides.epr_category_id !== "") {
    const n = Number(overrides.epr_category_id);
    if (!Number.isNaN(n)) out.epr_category_id = n;
  }
  return out;
}

const EN_MONTH_NAME_TO_CODE = {
  january: "1",
  february: "2",
  march: "3",
  april: "4",
  may: "5",
  june: "6",
  july: "7",
  august: "8",
  september: "9",
  october: "10",
  november: "11",
  december: "12",
};

/** Labels from GET /countries/report_periodicity/ → {@link buildPeriods} tokens. */
const BI_MONTHLY_LABEL_TO_CODE = {
  "bi-monthly (jan-feb)": "1bm",
  "bi-monthly (mar-apr)": "2bm",
  "bi-monthly (may-jun)": "3bm",
  "bi-monthly (jul-aug)": "4bm",
  "bi-monthly (sep-oct)": "5bm",
  "bi-monthly (nov-dec)": "6bm",
};

const BI_ANNUAL_LABEL_TO_CODE = {
  "bi-annual (jan-jun)": "1b",
  "bi-annual (jul-dec)": "2b",
};

/**
 * Infers period type from a Select value (legacy code or API label).
 * Bi-monthly labels must be detected before generic `y` / `b` checks — "monthly" contains "y".
 */
export function resolvePeriodTypeFromPeriodValue(value) {
  if (value == null || value === "" || value === "-") {
    return "";
  }
  const v = String(value);
  const vl = v.toLowerCase();
  if (vl.includes("bi-monthly") || v.includes("bm")) {
    return "bi-monthly";
  }
  if (vl.includes("bi-annual")) {
    return "bi-annual";
  }
  if (v.includes("q")) {
    return "quarter";
  }
  if (/^[1-4]p$/i.test(v) || /\bperiod\b/.test(vl)) {
    return "period";
  }
  if (v === "1y" || vl === "year") {
    return "year";
  }
  if (v.includes("y")) {
    return "year";
  }
  if (/^[1-2]b$/i.test(v)) {
    return "bi-annual";
  }
  if (v.includes("b")) {
    return "bi-annual";
  }
  return "month";
}

/**
 * API may return display strings (e.g. "January", "1 quarter", "Year"); map to legacy
 * period tokens used by Create VAT return / {@link buildPeriods} ("1".."12", "1q".."4q", "1y").
 */
export function mapReportPeriodLabelToLegacyValue(text) {
  if (text == null || text === "") {
    return "";
  }
  const s = String(text).trim();
  const sl = s.toLowerCase().replace(/[\u2013\u2014]/g, "-");
  if (sl === "year") {
    return "1y";
  }
  const q = /^([1-4])\s*quarter\s*$/i.exec(s);
  if (q) {
    return `${q[1]}q`;
  }
  const qRange = /^([1-4])\s*quarter\s*\(/i.exec(s);
  if (qRange) {
    return `${qRange[1]}q`;
  }
  const m = EN_MONTH_NAME_TO_CODE[sl];
  if (m) {
    return m;
  }
  const bmRange = /^([1-6])\s*bi-monthly\s*\(/i.exec(s);
  if (bmRange) {
    return `${bmRange[1]}bm`;
  }
  const pRange = /^([1-4])\s*period\s*\(/i.exec(s);
  if (pRange) {
    return `${pRange[1]}p`;
  }
  const baRange = /^([1-2])\s*bi-annual\s*\(/i.exec(s);
  if (baRange) {
    return `${baRange[1]}b`;
  }
  const bm = BI_MONTHLY_LABEL_TO_CODE[sl];
  if (bm) {
    return bm;
  }
  const ba = BI_ANNUAL_LABEL_TO_CODE[sl];
  if (ba) {
    return ba;
  }
  return s;
}

/**
 * Legacy Create VAT return: `period` in POST was `Number.parseInt(period, 10)` (e.g. 1 for "1q").
 * Returns a finite integer for JSON body (never `NaN` — that becomes `null` in JSON and breaks the API).
 */
export function periodStringToApiInt(period) {
  if (period == null || period === "" || period === "-") {
    return null;
  }
  if (typeof period === "number" && Number.isFinite(period)) {
    const t = Math.trunc(period);
    return Number.isNaN(t) ? null : t;
  }
  const n = Number.parseInt(String(period).trim(), 10);
  if (Number.isNaN(n)) {
    return null;
  }
  return n;
}

/** Grouped GET /countries/report_periodicity/ — keys like `quarter`, `month`, `year`, `period`, `bi-monthly`, … */
function isGroupedReportPeriodicityPayload(data) {
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }
  const legacyListKeys = [
    "items",
    "results",
    "data",
    "periods",
    "report_periodicity",
    "periodicity",
  ];
  if (legacyListKeys.some((key) => Array.isArray(data[key]))) {
    return false;
  }
  return Object.values(data).some((value) => Array.isArray(value));
}

function normalizeGroupedReportPeriodTypeKey(groupKey) {
  const raw = String(groupKey ?? "").trim();
  const normalized = raw.toLowerCase();

  if (normalized === "year") return "year";
  if (normalized === "quarter") return "quarter";
  if (normalized === "month") return "month";
  if (normalized === "period") return "period";
  if (normalized === "bi-annual" || normalized === "biannual") return "bi-annual";
  // Standard bi-monthly — distinct from even/uneven variants
  if (normalized === "bi-monthly") return "bi-monthly";
  if (normalized.includes("bi-monthly")) return raw;

  return raw;
}

function mapGroupedIndexToLegacyValue(periodTypeKey, indexKey) {
  const type = normalizeGroupedReportPeriodTypeKey(periodTypeKey);
  const index = String(indexKey ?? "").trim();
  if (type === "year") return "1y";
  if (type === "quarter") return `${index}q`;
  if (type === "month") return index;
  if (type === "period") return `${index}p`;
  if (type === "bi-monthly") return `${index}bm`;
  if (type.includes("bi-monthly")) return `${index}bm`;
  if (type === "bi-annual") return `${index}b`;
  return index;
}

function normalizeGroupedReportPeriodicityResponse(data) {
  const options = [];

  for (const [groupKey, entries] of Object.entries(data)) {
    if (!Array.isArray(entries)) {
      continue;
    }

    const periodType = normalizeGroupedReportPeriodTypeKey(groupKey);

    for (const entry of entries) {
      if (typeof entry === "string") {
        const label = entry.trim();
        if (!label) continue;
        options.push({
          value:
            mapReportPeriodLabelToLegacyValue(label) ||
            mapGroupedIndexToLegacyValue(groupKey, "1"),
          label,
          periodType,
        });
        continue;
      }

      if (entry && typeof entry === "object") {
        for (const [indexKey, label] of Object.entries(entry)) {
          const displayLabel = String(label ?? "").trim();
          if (!displayLabel) continue;
          options.push({
            value:
              mapReportPeriodLabelToLegacyValue(displayLabel) ||
              mapGroupedIndexToLegacyValue(groupKey, indexKey),
            label: displayLabel,
            periodType,
          });
        }
      }
    }
  }

  return options;
}

/** Period type for Assigned Reporting Periodicity filter (prefers API `periodType` on option). */
export function resolveReportPeriodOptionType(option) {
  if (option?.periodType != null && String(option.periodType).trim() !== "") {
    return String(option.periodType).trim();
  }
  return resolvePeriodTypeFromPeriodValue(option?.value);
}

/**
 * GET /countries/report_periodicity/ — value for Select must match legacy {@link buildPeriods} codes
 * (e.g. "1".."12", "1q".."4q", "1y") so POST /returns/declarations `period` matches pre–API-driven flow.
 * Prefers `code` / `value` over numeric `id`.
 * Plain string arrays (["Year","1 quarter",…,"December"]) are mapped by {@link mapReportPeriodLabelToLegacyValue}.
 * Grouped objects ({ quarter: [...], month: [...] }) use {@link normalizeGroupedReportPeriodicityResponse}.
 */
export function normalizeReportPeriodicityResponse(data) {
  if (data == null) return [];

  if (isGroupedReportPeriodicityPayload(data)) {
    return normalizeGroupedReportPeriodicityResponse(data);
  }

  const list = Array.isArray(data)
    ? data
    : data.items ||
      data.results ||
      data.data ||
      data.periods ||
      data.report_periodicity ||
      data.periodicity ||
      [];
  if (!Array.isArray(list)) return [];
  return list.map((item, i) => {
    if (typeof item === "string") {
      const label = item;
      const value = mapReportPeriodLabelToLegacyValue(label);
      return { value, label };
    }
    if (item && typeof item === "object") {
      const displayLabel = String(
        item.name ??
          item.label ??
          item.title ??
          item.description ??
          item.value ??
          i
      );
      const fromDisplay = mapReportPeriodLabelToLegacyValue(displayLabel);
      if (periodStringToApiInt(fromDisplay) != null) {
        return { value: fromDisplay, label: displayLabel };
      }
      let raw =
        item.code ??
        item.period_code ??
        item.value ??
        item.periodicity ??
        item.period;
      if (raw == null || raw === "") {
        if (item.month != null && item.month !== "") {
          raw = String(item.month);
        } else if (item.quarter != null && item.quarter !== "") {
          raw = `${item.quarter}q`;
        } else {
          raw = item.id ?? i;
        }
      }
      const value = String(raw);
      return { value, label: displayLabel || value };
    }
    return { value: String(item), label: String(item) };
  });
}
